import React, { useState, useEffect } from 'react';
import {
  Box, Button, Container, Typography, Stack, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Autocomplete, Paper, Chip, Grid, CircularProgress
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import Page from '../../components/Page';

const API_URL = window.location.origin;

export default function CalendarTab() {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [events, setEvents] = useState([]);
    const [matters, setMatters] = useState([]);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Modals
    const [openAddModal, setOpenAddModal] = useState(false);
    const [openViewModal, setOpenViewModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);

    // Form
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        eventDate: '',
        legalMatterId: null
    });

    // --- Data Fetching ---
    useEffect(() => {
        const fetchData = async () => {
            try {
                const userRes = await axios.get(`${API_URL}/api/user`, { withCredentials: true });
                setUser(userRes.data);

                const [eventsRes, mattersRes] = await Promise.all([
                    axios.get(`${API_URL}/api/calendar-events`),
                    axios.get(`${API_URL}/api/legal-matter`)
                ]);

                const allEvents = Array.isArray(eventsRes.data) ? eventsRes.data : [];
                // Filter events to only show the ones belonging to the logged-in user
                const myEvents = allEvents.filter(ev => ev.user?.id === userRes.data.id);
                setEvents(myEvents);
                
                setMatters(Array.isArray(mattersRes.data?.matters) ? mattersRes.data.matters : []);
            } catch (error) {
                console.error('Error fetching calendar data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // --- Calendar Generation ---
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayIndex = new Date(year, month, 1).getDay(); // 0 = Sunday

    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const days = [];
    for (let i = 0; i < firstDayIndex; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(i);

    const handlePrevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
    const handleNextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

    // --- Handlers ---
    const handleDayClick = (day) => {
        if (!day) return;
        const clickedDate = new Date(year, month, day);
        // Format to YYYY-MM-DD for input
        const yyyy = clickedDate.getFullYear();
        const mm = String(clickedDate.getMonth() + 1).padStart(2, '0');
        const dd = String(clickedDate.getDate()).padStart(2, '0');
        
        setFormData({ title: '', description: '', eventDate: `${yyyy}-${mm}-${dd}`, legalMatterId: null });
        setOpenAddModal(true);
    };

    const handleEventClick = (e, eventRecord) => {
        e.stopPropagation();
        setSelectedEvent(eventRecord);
        setOpenViewModal(true);
    };

    const handleSaveEvent = async () => {
        if (!formData.title || !formData.eventDate) return;

        try {
            const payload = {
                ...formData,
                legalMatterId: formData.legalMatterId?.id || null,
                userId: user?.id
            };

            const res = await axios.post(`${API_URL}/api/calendar-events`, payload);
            
            // Refresh events locally
            setEvents(prev => [...prev, {
                ...res.data, 
                legalMatter: formData.legalMatterId,
                user: { id: user?.id } // Ensure the local event retains the user ID so it doesn't disappear on save
            }]);
            
            setOpenAddModal(false);
        } catch (error) {
            console.error("Failed to save event:", error);
        }
    };

    const handleDeleteEvent = async (id) => {
        if (!window.confirm("Are you sure you want to delete this diary entry?")) return;
        
        try {
            await axios.delete(`${API_URL}/api/calendar-events/${id}`);
            setEvents(prev => prev.filter(e => e.id !== id));
            setOpenViewModal(false);
        } catch (error) {
            console.error("Failed to delete event:", error);
        }
    };

    // Helper: Filter events for a specific day
    const getEventsForDay = (day) => {
        if (!Array.isArray(events)) return [];
        return events.filter(e => {
            const d = new Date(e.eventDate);
            return d.getDate() === day && d.getMonth() === month && d.getFullYear() === year;
        });
    };

    if (loading) return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>;

    return (
        <Page title="Calendar & Diary">
            <Container maxWidth="xl">
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 4 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        Matter Diary & Calendar
                    </Typography>
                    <Button 
                        variant="contained" 
                        startIcon={<AddCircleOutlineIcon />} 
                        onClick={() => {
                            setFormData({ title: '', description: '', eventDate: '', legalMatterId: null });
                            setOpenAddModal(true);
                        }}
                    >
                        Add Diary Entry
                    </Button>
                </Stack>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fff' }}>
                    {/* Calendar Header Controls */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <IconButton onClick={handlePrevMonth}><ChevronLeftIcon /></IconButton>
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                            {monthNames[month]} {year}
                        </Typography>
                        <IconButton onClick={handleNextMonth}><ChevronRightIcon /></IconButton>
                    </Stack>

                    {/* Calendar Grid */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', bgcolor: '#e0e0e0', border: '1px solid #e0e0e0' }}>
                        {/* Day Names */}
                        {dayNames.map(dayName => (
                            <Box key={dayName} sx={{ bgcolor: '#f5f5f5', p: 1, textAlign: 'center', fontWeight: 'bold' }}>
                                {dayName}
                            </Box>
                        ))}

                        {/* Day Cells */}
                        {days.map((day, idx) => {
                            const dayEvents = day ? getEventsForDay(day) : [];
                            const isToday = day === new Date().getDate() && month === new Date().getMonth() && year === new Date().getFullYear();

                            return (
                                <Box 
                                    key={idx} 
                                    onClick={() => handleDayClick(day)}
                                    sx={{ 
                                        minHeight: '120px', 
                                        bgcolor: isToday ? '#f0f7ff' : '#fff', 
                                        p: 1, 
                                        cursor: day ? 'pointer' : 'default',
                                        '&:hover': day ? { bgcolor: '#fafafa' } : {}
                                    }}
                                >
                                    {day && (
                                        <>
                                            <Typography variant="body2" sx={{ fontWeight: isToday ? 'bold' : 'normal', color: isToday ? 'primary.main' : 'text.primary', mb: 1 }}>
                                                {day}
                                            </Typography>
                                            
                                            <Stack spacing={0.5}>
                                                {dayEvents.map(ev => (
                                                    <Chip 
                                                        key={ev.id}
                                                        label={ev.title}
                                                        size="small"
                                                        onClick={(e) => handleEventClick(e, ev)}
                                                        sx={{ 
                                                            justifyContent: 'flex-start', 
                                                            bgcolor: ev.legalMatter ? '#e8f5e9' : '#fff3e0',
                                                            border: '1px solid',
                                                            borderColor: ev.legalMatter ? '#4caf50' : '#ff9800',
                                                            fontSize: '0.7rem',
                                                            cursor: 'pointer'
                                                        }}
                                                    />
                                                ))}
                                            </Stack>
                                        </>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </Paper>

                {/* --- ADD DIARY ENTRY MODAL --- */}
                <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)} fullWidth maxWidth="sm">
                    <DialogTitle>Add Diary Entry</DialogTitle>
                    <DialogContent dividers>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Title / Event Name" required value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} sx={{ mt: 1 }} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth type="date" label="Date" required InputLabelProps={{ shrink: true }} value={formData.eventDate} onChange={(e) => setFormData({...formData, eventDate: e.target.value})} />
                            </Grid>
                            <Grid item xs={12}>
                                <Autocomplete
                                    options={matters}
                                    getOptionLabel={(option) => `${option.matterName} (${option.estateNo || 'N/A'})`}
                                    value={formData.legalMatterId}
                                    onChange={(e, newValue) => setFormData({ ...formData, legalMatterId: newValue })}
                                    renderInput={(params) => <TextField {...params} label="Link to Estate / Matter (Optional)" placeholder="Search matters..." />}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Description / Notes" multiline minRows={3} value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenAddModal(false)}>Cancel</Button>
                        <Button onClick={handleSaveEvent} variant="contained" disabled={!formData.title || !formData.eventDate}>Save Entry</Button>
                    </DialogActions>
                </Dialog>

                {/* --- VIEW DIARY ENTRY MODAL --- */}
                <Dialog open={openViewModal} onClose={() => setOpenViewModal(false)} fullWidth maxWidth="sm">
                    {selectedEvent && (
                        <>
                            <DialogTitle sx={{ fontWeight: 'bold' }}>{selectedEvent.title}</DialogTitle>
                            <DialogContent dividers>
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Date</Typography>
                                        <Typography variant="body1">{new Date(selectedEvent.eventDate).toLocaleDateString('en-ZA')}</Typography>
                                    </Box>
                                    
                                    {selectedEvent.legalMatter && (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Linked Matter</Typography>
                                            <Typography variant="body1" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                                                {selectedEvent.legalMatter.matterName} ({selectedEvent.legalMatter.estateNo || 'No Ref'})
                                            </Typography>
                                        </Box>
                                    )}

                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Description</Typography>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedEvent.description || 'No notes provided.'}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </DialogContent>
                            <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
                                <Button color="error" startIcon={<DeleteIcon />} onClick={() => handleDeleteEvent(selectedEvent.id)}>Delete</Button>
                                <Button variant="contained" onClick={() => setOpenViewModal(false)}>Close</Button>
                            </DialogActions>
                        </>
                    )}
                </Dialog>

            </Container>
        </Page>
    );
}
